'use client';

import React from 'react';

import styles from '../../styles/components/errorBoundary.module.scss';

// След деплой Cloudflare може да сервира stale HTML, сочещ изтрити хеширани
// chunks → ChunkLoadError при hydration. Еднократен reload взима свежия HTML.
// Timestamp guard в sessionStorage пази от reload цикъл (макс 1 на 30 сек).
const CHUNK_RELOAD_TS_KEY = 'chunkErrorReloadedAt';
const CHUNK_RELOAD_LOOP_WINDOW_MS = 30000;

function isChunkLoadError(error) {
  if (!error) {
    return false;
  }
  const message = String(error.message || error);
  return (
    error.name === 'ChunkLoadError' ||
    /Loading (?:CSS )?chunk .* failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    if (typeof window !== 'undefined' && isChunkLoadError(error)) {
      try {
        const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_TS_KEY) || 0);
        if (Date.now() - lastReloadAt > CHUNK_RELOAD_LOOP_WINDOW_MS) {
          sessionStorage.setItem(CHUNK_RELOAD_TS_KEY, String(Date.now()));
          window.location.reload();
          return;
        }
      } catch {
        // sessionStorage недостъпен (private mode) → показваме fallback-а
        // вместо да рискуваме reload цикъл.
      }
    }

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // data-nosnippet: Google не бива да ползва error текста като снипет на сайта
      // (случвало се е). Текстът е хардкоднат по изключение от admin-editable
      // правилото — когато boundary-то падне, SWR/провайдърите/админ данните са мъртви.
      return (
        <div className={styles.errorContainer} data-nosnippet>
          <h2>Възникна грешка.</h2>
          <p>Моля, презаредете страницата или опитайте отново по-късно.</p>
          <button type="button" onClick={this.handleReload} className={styles.reloadButton}>
            Презареди страницата
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
